import { BaseViewModel } from "@iot-platform/core";
import { ReportGpsTypeEnum } from "@report/domain/models/report-route-gps.ts";
import type {
    Report,
    ReportHistoricalCustomFilter,
    ReportHistoricalFilter,
} from "@report/domain/models/report.model.ts";
import { ReportGetHistoricalCustomUseCase } from "@report/domain/usecases/report-get-historical-custom.usecase.ts";
import { ReportGetDailyActivityGpsUsecase } from "@report/domain/usecases/report-get-historical-daily-activity-gps.usecase.ts";
import { ReportGetHistoricalDetailStateGpsUsecase } from "@report/domain/usecases/report-get-historical-detailed-state-gps.usecase.ts";
import { ReportGetHistoricalPoisRoutesUsecase } from "@report/domain/usecases/report-get-historical-pois-routes.usecase.ts";
import { ReportGetHistoricalPoisVisitedUsecase } from "@report/domain/usecases/report-get-historical-pois-visited.usecase.ts";
import { ReportGetHistoricalStateGpsUsecase } from "@report/domain/usecases/report-get-historical-state-gps.usecase.ts";
import { ReportGetTraveledKilometersUsecase } from "@report/domain/usecases/report-get-historical-traveled-kilometers.usecase.ts";
import { ReportGetHistoricalUseCase } from "@report/domain/usecases/report-get-historical.usecase.ts";
import { ReportHistoricalRequestMailUseCase } from "@report/domain/usecases/report-historical-request-mail.usecase.ts";
import { inject, injectable } from "inversify";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

export const defaultToKm = 1000;

@injectable()
export class HistoricalViewmodel extends BaseViewModel {
    @observable
    report: Report[] = [];

    @observable
    isModalFilterVisible = false;

    @observable private from = 0;
    @observable private to = 0;
    private stationIds: number[] = [];
    private reportTypes: Record<string, string> = {};

    @observable
    sensors: number[] = [];

    @observable
    road?: string;

    @observable
    initialLoading = true;

    @observable
    isUpdatingReport = false;

    @observable
    gpsReportType?: ReportGpsTypeEnum;

    @observable
    originPoi?: string;

    @observable
    fromKm?: number = 0;

    @observable
    toKm?: number = defaultToKm;

    constructor(
        @inject(ReportGetHistoricalUseCase)
        private readonly reportGetHistoricalUseCase: ReportGetHistoricalUseCase,
        @inject(ReportHistoricalRequestMailUseCase)
        private readonly reportHistoricalRequestMailUseCase: ReportHistoricalRequestMailUseCase,
        @inject(ReportGetHistoricalCustomUseCase)
        private readonly reportGetHistoricalCustomUseCase: ReportGetHistoricalCustomUseCase,
        @inject(ReportGetDailyActivityGpsUsecase)
        private readonly reportGetDailyActivityGpsUsecase: ReportGetDailyActivityGpsUsecase,
        @inject(ReportGetHistoricalDetailStateGpsUsecase)
        private readonly reportGetHistoricalDetailStateGpsUsecase: ReportGetHistoricalDetailStateGpsUsecase,
        @inject(ReportGetHistoricalPoisRoutesUsecase)
        private readonly reportGetHistoricalPoisRoutesUsecase: ReportGetHistoricalPoisRoutesUsecase,
        @inject(ReportGetHistoricalPoisVisitedUsecase)
        private readonly reportGetHistoricalPoisVisitedUsecase: ReportGetHistoricalPoisVisitedUsecase,
        @inject(ReportGetHistoricalStateGpsUsecase)
        private readonly reportGetHistoricalStateGpsUsecase: ReportGetHistoricalStateGpsUsecase,
        @inject(ReportGetTraveledKilometersUsecase)
        private readonly reportGetHistoricalTraveledKilometersUsecase: ReportGetTraveledKilometersUsecase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        if (this.sensors.length) {
            await this.fetchCustomReport();
        } else if (this.gpsReportType) {
            switch (this.gpsReportType) {
                case ReportGpsTypeEnum.dailyActivityGps:
                    await this.fetchDailyActivityGpsReport();
                    break;
                case ReportGpsTypeEnum.statesGps:
                    await this.fetchStatesGpsReport();
                    break;
                case ReportGpsTypeEnum.detailedStatesGps:
                    await this.fetchDetailedStatesGpsReport();
                    break;
                case ReportGpsTypeEnum.traveledKilometers:
                    await this.fetchTraveledKilometersReport();
                    break;
                case ReportGpsTypeEnum.poisVisited:
                    await this.fetchPoisVisitedReport();
                    break;
                case ReportGpsTypeEnum.poisRoutes:
                    await this.fetchPoisRoutesReport();
                    break;
                default:
                    return;
            }
        } else {
            await this.fetchReport();
        }

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    async fetchReport(): Promise<void> {
        const reportHistoricalFilter: ReportHistoricalFilter = {
            from: this.from,
            to: this.to,
            stationIds: this.stationIds,
        };

        if (this.road) {
            reportHistoricalFilter.road = this.road;
            if (this.fromKm !== undefined) {
                reportHistoricalFilter.fromPk = this.fromKm;
            }

            if (this.toKm !== undefined) {
                reportHistoricalFilter.toPk = this.toKm;
            }
        }

        const report = await this.reportGetHistoricalUseCase.execute(
            reportHistoricalFilter,
        );
        runInAction(() => {
            this.report = report;
        });
    }

    async fetchCustomReport(): Promise<void> {
        const stationId = this.stationIds[0];

        if (!stationId) {
            return;
        }

        const booleanFilters: Partial<ReportHistoricalCustomFilter> =
            Object.fromEntries(
                Object.entries(this.reportTypes).map(([key, value]) => [
                    key,
                    Boolean(value),
                ]),
            );

        const historicalCustomFilters: ReportHistoricalCustomFilter = {
            from: this.from,
            to: this.to,
            stationId,
            variableIds: this.sensors,
            page: 1,
            maxValues: false,
            minValues: false,
            avgValues: false,
            sumValues: false,
            measures: false,
            totalSum: false,
            tendency: false,
            ...booleanFilters,
        };

        if (this.road) {
            historicalCustomFilters.road = this.road;
            if (this.fromKm !== undefined) {
                historicalCustomFilters.fromPk = this.fromKm;
            }

            if (this.toKm !== undefined) {
                historicalCustomFilters.toPk = this.toKm;
            }
        }

        const report = await this.reportGetHistoricalCustomUseCase.execute(
            historicalCustomFilters,
        );
        runInAction(() => {
            this.report = report;
        });
    }

    async fetchDailyActivityGpsReport(): Promise<void> {
        const reportHistoricalFilter: ReportHistoricalFilter = {
            from: this.from,
            to: this.to,
            stationIds: this.stationIds,
        };

        if (this.road) {
            reportHistoricalFilter.road = this.road;
            if (this.fromKm !== undefined) {
                reportHistoricalFilter.fromPk = this.fromKm;
            }

            if (this.toKm !== undefined) {
                reportHistoricalFilter.toPk = this.toKm;
            }
        }

        const report = await this.reportGetDailyActivityGpsUsecase.execute(
            reportHistoricalFilter,
        );

        runInAction(() => {
            this.report = report;
        });
    }

    async fetchStatesGpsReport(): Promise<void> {
        const reportHistoricalFilter: ReportHistoricalFilter = {
            from: this.from,
            to: this.to,
            stationIds: this.stationIds,
        };

        if (this.road) {
            reportHistoricalFilter.road = this.road;
            if (this.fromKm !== undefined) {
                reportHistoricalFilter.fromPk = this.fromKm;
            }

            if (this.toKm !== undefined) {
                reportHistoricalFilter.toPk = this.toKm;
            }
        }

        const report = await this.reportGetHistoricalStateGpsUsecase.execute(
            reportHistoricalFilter,
        );
        runInAction(() => {
            this.report = report;
        });
    }

    async fetchDetailedStatesGpsReport(): Promise<void> {
        const reportHistoricalFilter: ReportHistoricalFilter = {
            from: this.from,
            to: this.to,
            stationIds: this.stationIds,
        };

        if (this.road) {
            reportHistoricalFilter.road = this.road;
            if (this.fromKm !== undefined) {
                reportHistoricalFilter.fromPk = this.fromKm;
            }

            if (this.toKm !== undefined) {
                reportHistoricalFilter.toPk = this.toKm;
            }
        }

        const report =
            await this.reportGetHistoricalDetailStateGpsUsecase.execute(
                reportHistoricalFilter,
            );
        runInAction(() => {
            this.report = report;
        });
    }

    async fetchTraveledKilometersReport(): Promise<void> {
        const reportHistoricalFilter: ReportHistoricalFilter = {
            from: this.from,
            to: this.to,
            stationIds: this.stationIds,
        };

        if (this.road) {
            reportHistoricalFilter.road = this.road;
            if (this.fromKm !== undefined) {
                reportHistoricalFilter.fromPk = this.fromKm;
            }

            if (this.toKm !== undefined) {
                reportHistoricalFilter.toPk = this.toKm;
            }
        }

        const report =
            await this.reportGetHistoricalTraveledKilometersUsecase.execute(
                reportHistoricalFilter,
            );
        runInAction(() => {
            this.report = report;
        });
    }

    async fetchPoisVisitedReport(): Promise<void> {
        const reportHistoricalFilter: ReportHistoricalFilter = {
            from: this.from,
            to: this.to,
            stationIds: this.stationIds,
        };

        if (this.road) {
            reportHistoricalFilter.road = this.road;
            if (this.fromKm !== undefined) {
                reportHistoricalFilter.fromPk = this.fromKm;
            }

            if (this.toKm !== undefined) {
                reportHistoricalFilter.toPk = this.toKm;
            }
        }

        const report = await this.reportGetHistoricalPoisVisitedUsecase.execute(
            reportHistoricalFilter,
        );
        runInAction(() => {
            this.report = report;
        });
    }

    async fetchPoisRoutesReport(): Promise<void> {
        const reportHistoricalFilter: ReportHistoricalFilter = {
            from: this.from,
            to: this.to,
            stationIds: this.stationIds,
        };

        if (this.road) {
            reportHistoricalFilter.road = this.road;
            if (this.fromKm !== undefined) {
                reportHistoricalFilter.fromPk = this.fromKm;
            }

            if (this.toKm !== undefined) {
                reportHistoricalFilter.toPk = this.toKm;
            }
        }

        if (this.originPoi) {
            reportHistoricalFilter.originPoi = this.originPoi;
        }

        const report = await this.reportGetHistoricalPoisRoutesUsecase.execute(
            reportHistoricalFilter,
        );
        runInAction(() => {
            this.report = report;
        });
    }

    @action
    setFrom(from: string): void {
        this.from = Number(from);
    }

    @action
    setTo(to: string): void {
        this.to = Number(to);
    }

    @computed
    get getFrom(): number {
        return this.from;
    }

    @computed
    get getTo(): number {
        return this.to;
    }

    @action
    setStationIds(stationIds: number[]): void {
        this.stationIds = stationIds;
    }

    @action
    showModalFilter(show: boolean): void {
        runInAction(() => {
            this.isModalFilterVisible = show;
        });
    }

    async sendReportByEmail(): Promise<void> {
        const booleanFilters: Partial<ReportHistoricalCustomFilter> =
            Object.fromEntries(
                Object.entries(this.reportTypes).map(([key, value]) => [
                    key,
                    Boolean(value),
                ]),
            );

        const historicalCustomFilters: ReportHistoricalFilter = {
            from: this.from,
            to: this.to,
            stationIds: this.stationIds,
            variableIds: this.sensors,
            page: 1,
            maxValues: false,
            minValues: false,
            avgValues: false,
            sumValues: false,
            measures: false,
            totalSum: false,
            tendency: false,
            ...booleanFilters,
        };

        await this.reportHistoricalRequestMailUseCase.execute(
            historicalCustomFilters,
        );
    }

    @action
    setIsUpdatingReport(isUpdatingReport: boolean): void {
        runInAction(() => {
            this.isUpdatingReport = isUpdatingReport;
        });
    }

    @action
    setSensors(sensors: number[]): void {
        runInAction(() => {
            this.sensors = sensors;
        });
    }

    @action
    setReportTypes(reportTypes: Record<string, string>): void {
        runInAction(() => {
            this.reportTypes = reportTypes;
        });
    }

    @action
    setRoad(road: string): void {
        runInAction(() => {
            this.road = road;
        });
    }

    @action
    setGpsType(gpsReportType: ReportGpsTypeEnum): void {
        runInAction(() => {
            this.gpsReportType = gpsReportType;
        });
    }

    @action
    setOriginPoi(originPoi: string): void {
        runInAction(() => {
            this.originPoi = originPoi;
        });
    }

    @action
    setFromKm(fromKm: number): void {
        runInAction(() => {
            this.fromKm = fromKm;
        });
    }

    @action
    setToKm(toKm: number): void {
        runInAction(() => {
            this.toKm = toKm;
        });
    }
}
